import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LuArrowLeft, LuPin, LuMessageCircle, LuTrash2, LuSend, LuPlus, LuX, LuEye } from "react-icons/lu";
import "./discussion.css";
import usePullToRefresh from "../../hooks/usePullToRefresh";

const timeAgo = (date) => {
  const seconds = Math.floor((Date.now() - new Date(date)) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

const Discussion = ({ isLoggedIn, loginUser, onLogout }) => {
  const navigate = useNavigate();
  const [discussions, setDiscussions] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(true);

  // New topic form
  const [showForm, setShowForm] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newBody, setNewBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  // Comment input
  const [commentText, setCommentText] = useState("");
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const commentsEndRef = useRef(null);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login");
    } else {
      localStorage.setItem("lastSeenCommunity", new Date().toISOString());
    }
  }, [isLoggedIn, navigate]);

  const fetchDiscussions = useCallback(async (before) => {
    try {
      setLoading(true);
      const params = {};
      if (before) params.before = before;
      const res = await axios.get("/api/v1/discussions", { params });
      const fetched = res.data.discussions || [];
      if (before) {
        setDiscussions((prev) => [...prev, ...fetched]);
      } else {
        setDiscussions(fetched);
      }
      setHasMore(fetched.length >= 20);
      setError("");
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onLogout();
        navigate("/login");
      } else {
        setError("Failed to load topics.");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, onLogout]);

  const handleRefresh = useCallback(async () => {
    setHasMore(true);
    await fetchDiscussions();
  }, [fetchDiscussions]);

  const { isRefreshing, pullDistance, containerRef } = usePullToRefresh(handleRefresh);

  useEffect(() => {
    if (isLoggedIn) fetchDiscussions();
  }, [isLoggedIn, fetchDiscussions]);

  const loadMore = () => {
    if (discussions.length === 0 || !hasMore) return;
    const lastItem = discussions[discussions.length - 1];
    fetchDiscussions(lastItem.lastActivityAt);
  };

  const openTopic = async (topic) => {
    try {
      setLoadingDetail(true);
      setSelectedTopic(topic);
      setComments([]);
      const res = await axios.get(`/api/v1/discussions/${topic._id}`);
      setSelectedTopic(res.data.discussion);
      setComments(res.data.comments || []);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onLogout();
        navigate("/login");
      }
    } finally {
      setLoadingDetail(false);
    }
  };

  const goBack = () => {
    setSelectedTopic(null);
    setComments([]);
    setCommentText("");
    // Refresh list to get updated comment counts
    fetchDiscussions();
  };

  // Create topic
  const handleCreateTopic = async (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !newBody.trim()) return;
    try {
      setSubmitting(true);
      setFormError("");
      const res = await axios.post("/api/v1/discussions", {
        title: newTitle.trim(),
        body: newBody.trim(),
      });
      setDiscussions((prev) => [res.data.discussion, ...prev]);
      setNewTitle("");
      setNewBody("");
      setShowForm(false);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onLogout();
        navigate("/login");
      } else {
        setFormError(err.response?.data?.message || "Failed to create topic.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Add comment
  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || !selectedTopic) return;
    try {
      setCommentSubmitting(true);
      const res = await axios.post(`/api/v1/discussions/${selectedTopic._id}/comments`, {
        content: commentText.trim(),
      });
      setComments((prev) => [...prev, res.data.comment]);
      setSelectedTopic((prev) => ({ ...prev, commentCount: (prev.commentCount || 0) + 1 }));
      setCommentText("");
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onLogout();
        navigate("/login");
      }
    } finally {
      setCommentSubmitting(false);
    }
  };

  // Delete topic
  const handleDeleteTopic = async (id) => {
    try {
      await axios.delete(`/api/v1/discussions/${id}`);
      setDiscussions((prev) => prev.filter((d) => d._id !== id));
      if (selectedTopic && selectedTopic._id === id) {
        goBack();
      }
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onLogout();
        navigate("/login");
      }
    }
  };

  // Delete comment
  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`/api/v1/discussions/${selectedTopic._id}/comments/${commentId}`);
      setComments((prev) => prev.filter((c) => c._id !== commentId));
      setSelectedTopic((prev) => ({ ...prev, commentCount: Math.max(0, (prev.commentCount || 1) - 1) }));
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onLogout();
        navigate("/login");
      }
    }
  };

  // Pin/Unpin (admin only)
  const handleTogglePin = async (id) => {
    try {
      const res = await axios.put(`/api/v1/discussions/${id}/pin`);
      setDiscussions((prev) =>
        prev.map((d) => (d._id === id ? { ...d, isPinned: res.data.isPinned } : d))
      );
      if (selectedTopic && selectedTopic._id === id) {
        setSelectedTopic((prev) => ({ ...prev, isPinned: res.data.isPinned }));
      }
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onLogout();
        navigate("/login");
      }
    }
  };

  const canDelete = (authorId) => {
    return loginUser?._id === authorId || loginUser?.role === "admin";
  };

  if (!isLoggedIn) return null;

  // ── Detail View ──
  if (selectedTopic) {
    return (
      <div className="sr-discussion">
        <div className="sr-discussion__top">
          <div className="sr-discussion__top-content">
            <button className="sr-discussion__back" onClick={goBack}>
              <LuArrowLeft />
              <span>Community</span>
            </button>
          </div>
        </div>

        <div className="sr-discussion__body">
          <div className="sr-discussion__detail-card">
            <div className="sr-discussion__detail-header">
              {selectedTopic.isPinned && (
                <span className="sr-discussion__pin-badge"><LuPin size={12} /> Pinned</span>
              )}
              <h2 className="sr-discussion__detail-title">{selectedTopic.title}</h2>
              <div className="sr-discussion__detail-meta">
                <img
                  src={selectedTopic.author?.avatar}
                  alt=""
                  className="sr-discussion__avatar sr-discussion__avatar--sm"
                />
                <span className="sr-discussion__author-name">{selectedTopic.author?.userName}</span>
                <span className="sr-discussion__time">{timeAgo(selectedTopic.createdAt)}</span>
                <span className="sr-discussion__meta-dot" />
                <LuEye size={13} />
                <span>{selectedTopic.viewCount || 0}</span>
              </div>
            </div>
            <p className="sr-discussion__detail-body">{selectedTopic.body}</p>
            <div className="sr-discussion__detail-actions">
              {loginUser?.role === "admin" && (
                <button className="sr-discussion__action-btn" onClick={() => handleTogglePin(selectedTopic._id)}>
                  <LuPin size={14} />
                  {selectedTopic.isPinned ? "Unpin" : "Pin"}
                </button>
              )}
              {canDelete(selectedTopic.author?._id) && (
                <button className="sr-discussion__action-btn sr-discussion__action-btn--danger" onClick={() => handleDeleteTopic(selectedTopic._id)}>
                  <LuTrash2 size={14} />
                  Delete
                </button>
              )}
            </div>
          </div>

          {/* Comments */}
          <div className="sr-discussion__comments-section">
            <h3 className="sr-discussion__comments-title">
              Comments ({selectedTopic.commentCount || 0})
            </h3>

            {loadingDetail ? (
              <div className="sr-discussion__loading">Loading comments...</div>
            ) : comments.length === 0 ? (
              <p className="sr-discussion__empty-comments">No comments yet. Start the conversation!</p>
            ) : (
              <div className="sr-discussion__comments-list">
                {comments.map((comment) => (
                  <div key={comment._id} className="sr-discussion__comment">
                    <img
                      src={comment.author?.avatar}
                      alt=""
                      className="sr-discussion__avatar sr-discussion__avatar--xs"
                    />
                    <div className="sr-discussion__comment-body">
                      <div className="sr-discussion__comment-header">
                        <span className="sr-discussion__author-name">{comment.author?.userName}</span>
                        <span className="sr-discussion__time">{timeAgo(comment.createdAt)}</span>
                        {canDelete(comment.author?._id) && (
                          <button
                            className="sr-discussion__comment-delete"
                            onClick={() => handleDeleteComment(comment._id)}
                            aria-label="Delete comment"
                          >
                            <LuTrash2 size={12} />
                          </button>
                        )}
                      </div>
                      <p className="sr-discussion__comment-text">{comment.content}</p>
                    </div>
                  </div>
                ))}
                <div ref={commentsEndRef} />
              </div>
            )}
          </div>

          {/* Comment Input */}
          <form className="sr-discussion__comment-form" onSubmit={handleAddComment}>
            <input
              type="text"
              className="sr-discussion__comment-input"
              placeholder="Write a comment..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              maxLength={1000}
              disabled={commentSubmitting}
            />
            <button
              type="submit"
              className="sr-discussion__comment-submit"
              disabled={!commentText.trim() || commentSubmitting}
            >
              <LuSend size={18} />
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── List View ──
  return (
    <div className="sr-discussion" ref={containerRef}>
      <div className="sr-discussion__top">
        <div className="sr-discussion__top-content">
          <h1 className="sr-discussion__title">Community</h1>
          <p className="sr-discussion__subtitle">Ask questions and connect with fellow commuters</p>
        </div>
      </div>

      <div className="sr-discussion__body">
        {(pullDistance > 0 || isRefreshing) && (
          <div className="sr-discussion__pull-indicator" style={{ height: pullDistance }}>
            {isRefreshing ? "Refreshing..." : "Pull to refresh"}
          </div>
        )}
        {/* New Topic Button / Form */}
        {!showForm ? (
          <button className="sr-discussion__new-btn" onClick={() => setShowForm(true)}>
            <LuPlus size={18} />
            New Topic
          </button>
        ) : (
          <form className="sr-discussion__form" onSubmit={handleCreateTopic}>
            <div className="sr-discussion__form-header">
              <span className="sr-discussion__form-label">New Topic</span>
              <button type="button" className="sr-discussion__form-close" onClick={() => { setShowForm(false); setFormError(""); }}>
                <LuX size={18} />
              </button>
            </div>
            <input
              type="text"
              className="sr-discussion__form-input"
              placeholder="Topic title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              maxLength={150}
              autoFocus
            />
            <textarea
              className="sr-discussion__form-textarea"
              placeholder="What's on your mind?"
              value={newBody}
              onChange={(e) => setNewBody(e.target.value)}
              maxLength={2000}
              rows={4}
            />
            {formError && <p className="sr-discussion__form-error">{formError}</p>}
            <button
              type="submit"
              className="sr-discussion__form-submit"
              disabled={!newTitle.trim() || !newBody.trim() || submitting}
            >
              {submitting ? "Posting..." : "Post Topic"}
            </button>
          </form>
        )}

        {/* Error */}
        {error && <p className="sr-discussion__error">{error}</p>}

        {/* Loading */}
        {loading && discussions.length === 0 ? (
          <div className="sr-discussion__skeleton-list">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="sr-discussion__skeleton-card">
                <div className="sr-discussion__skeleton-line sr-discussion__skeleton-line--title" />
                <div className="sr-discussion__skeleton-line sr-discussion__skeleton-line--meta" />
              </div>
            ))}
          </div>
        ) : discussions.length === 0 ? (
          <div className="sr-discussion__empty">
            <LuMessageCircle size={40} />
            <p>No topics yet. Be the first to start a discussion!</p>
          </div>
        ) : (
          <>
            <div className="sr-discussion__list">
              {discussions.map((d) => (
                <div
                  key={d._id}
                  className={`sr-discussion__card ${d.isPinned ? "sr-discussion__card--pinned" : ""}`}
                  onClick={() => openTopic(d)}
                >
                  <div className="sr-discussion__card-content">
                    {d.isPinned && (
                      <span className="sr-discussion__pin-badge"><LuPin size={11} /> Pinned</span>
                    )}
                    <h3 className="sr-discussion__card-title">{d.title}</h3>
                    <div className="sr-discussion__card-meta">
                      <img src={d.author?.avatar} alt="" className="sr-discussion__avatar sr-discussion__avatar--xs" />
                      <span className="sr-discussion__author-name">{d.author?.userName}</span>
                      <span className="sr-discussion__meta-dot" />
                      <LuMessageCircle size={13} />
                      <span>{d.commentCount || 0}</span>
                      <span className="sr-discussion__meta-dot" />
                      <LuEye size={13} />
                      <span>{d.viewCount || 0}</span>
                      <span className="sr-discussion__meta-dot" />
                      <span className="sr-discussion__time">{timeAgo(d.lastActivityAt)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {hasMore && (
              <button className="sr-discussion__load-more" onClick={loadMore} disabled={loading}>
                {loading ? "Loading..." : "Load More"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Discussion;
